import { eq } from "drizzle-orm";
import { db } from "./index";
import { users, type User, type NewUser } from "./schema";

/**
 * Get or create a user by Clerk ID
 * This is called when a user signs in to sync Clerk data with our DB
 */
export async function getOrCreateUser(clerkId: string, email: string, name?: string): Promise<User> {
  // Try to find existing user
  const existingUsers = await db
    .select()
    .from(users)
    .where(eq(users.clerkId, clerkId))
    .limit(1);

  if (existingUsers.length > 0) {
    return existingUsers[0];
  }

  // Create new user
  const newUsers = await db
    .insert(users)
    .values({
      clerkId,
      email,
      name: name || null,
    })
    .returning();

  return newUsers[0];
}

/**
 * Get user by Clerk ID
 */
export async function getUserByClerkId(clerkId: string): Promise<User | null> {
  const result = await db
    .select()
    .from(users)
    .where(eq(users.clerkId, clerkId))
    .limit(1);

  return result[0] || null;
}

/**
 * Get user by internal ID
 */
export async function getUserById(id: string): Promise<User | null> {
  const result = await db
    .select()
    .from(users)
    .where(eq(users.id, id))
    .limit(1);

  return result[0] || null;
}

/**
 * Update user profile
 */
export async function updateUser(
  clerkId: string,
  data: Partial<Pick<NewUser, "name" | "email">>
): Promise<User | null> {
  const result = await db
    .update(users)
    .set(data)
    .where(eq(users.clerkId, clerkId))
    .returning();

  return result[0] || null;
}

/**
 * Update user credits
 */
export async function updateUserCredits(
  clerkId: string,
  creditsRemaining: number
): Promise<User | null> {
  const result = await db
    .update(users)
    .set({ creditsRemaining })
    .where(eq(users.clerkId, clerkId))
    .returning();

  return result[0] || null;
}

/**
 * Deduct credits from user
 * Returns the updated user or null if insufficient credits
 */
export async function deductCredits(
  clerkId: string,
  amount: number
): Promise<User | null> {
  const user = await getUserByClerkId(clerkId);
  
  if (!user || user.creditsRemaining < amount) {
    return null;
  }

  return updateUserCredits(clerkId, user.creditsRemaining - amount);
}

/**
 * Update user subscription tier
 */
export async function updateSubscriptionTier(
  clerkId: string,
  tier: User["subscriptionTier"]
): Promise<User | null> {
  const result = await db
    .update(users)
    .set({ subscriptionTier: tier })
    .where(eq(users.clerkId, clerkId))
    .returning();

  return result[0] || null;
}
